import {
  Alert,
  Anchor,
  Badge,
  Box,
  Breadcrumbs,
  Button,
  Container,
  Group,
  NumberInput,
  Paper,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Textarea,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  IconArrowRight,
  IconBook2,
  IconCalculator,
  IconFileText,
  IconInfoCircle,
  IconSchool,
  IconSparkles,
} from "@tabler/icons-react";
import { ReactNode, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "../../seo/Seo";
import {
  buildCanonicalUrl,
  createBreadcrumbSchema,
  createFaqSchema,
} from "../../seo/utils";
import "./ReadingTimeCalculatorPage.css";

type NumberValue = number | "";
type ReadingMode = "text" | "words" | "pages" | "wpm";
type PresetSpeedKey = keyof typeof readingSpeeds;
type ReadingSpeedKey = PresetSpeedKey | "custom";
type WordsPerPageKey = "standard" | "doubleSpaced" | "singleSpaced" | "textbook" | "custom";

const TOOL_PATH = "/tools/reading-time-calculator";
const TOOLS_PATH = "/tools";

export const readingSpeeds = {
  slow: 150,
  average: 200,
  good: 250,
  fast: 300,
};

const speedOptions: Array<{ value: ReadingSpeedKey; label: string }> = [
  { value: "slow", label: "Slow reader - 150 words per minute" },
  { value: "average", label: "Average reader - 200 words per minute" },
  { value: "good", label: "Good reader - 250 words per minute" },
  { value: "fast", label: "Fast reader - 300 words per minute" },
  { value: "custom", label: "Custom speed" },
];

const wordsPerPageOptions: Array<{ value: WordsPerPageKey; label: string; words: number | null }> = [
  { value: "standard", label: "Standard book page - 250 words/page", words: 250 },
  { value: "doubleSpaced", label: "Academic double-spaced page - 250 words/page", words: 250 },
  { value: "singleSpaced", label: "Academic single-spaced page - 500 words/page", words: 500 },
  { value: "textbook", label: "Dense textbook page - 600 words/page", words: 600 },
  { value: "custom", label: "Custom words per page", words: null },
];

const speedLabels: Record<PresetSpeedKey, string> = {
  slow: "Slow reader",
  average: "Average reader",
  good: "Good reader",
  fast: "Fast reader",
};

const faqs = [
  {
    question: "How do I calculate reading time?",
    answer:
      "To calculate reading time, divide the total word count by your reading speed in words per minute. For example, 1000 words divided by 200 words per minute equals 5 minutes.",
  },
  {
    question: "How long does it take to read 1000 words?",
    answer:
      "1000 words usually takes around 4 to 7 minutes to read. At an average reading speed of 200 words per minute, it takes about 5 minutes.",
  },
  {
    question: "How long does it take to read one page?",
    answer:
      "One page can take around 1 to 3 minutes to read depending on the number of words on the page and your reading speed.",
  },
  {
    question: "How do I calculate reading time based on pages?",
    answer:
      "Multiply the number of pages by the estimated words per page, then divide by your reading speed. For example, 10 pages with 250 words per page equals 2500 words. At 200 words per minute, it takes about 12 minutes and 30 seconds.",
  },
  {
    question: "What is the average reading speed?",
    answer:
      "A common average reading speed is around 200 to 250 words per minute. Difficult academic or technical material may take longer to read.",
  },
  {
    question: "Can I calculate reading time from pasted text?",
    answer:
      "Yes. Paste your text into the calculator, and it will count the words and estimate the reading time based on your selected reading speed.",
  },
  {
    question: "Can this calculator estimate book reading time?",
    answer:
      "Yes. You can use the pages-to-reading-time mode to estimate how long it will take to read book chapters or a full book.",
  },
  {
    question: "Is reading time accurate?",
    answer:
      "Reading time is an estimate. Actual time can change based on reading speed, topic difficulty, focus level, note-taking, and whether you reread sections.",
  },
];

const readingTimeRows = [
  ["500 words", "3 min 20 sec", "2 min 30 sec", "2 min", "1 min 40 sec"],
  ["1000 words", "6 min 40 sec", "5 min", "4 min", "3 min 20 sec"],
  ["1500 words", "10 min", "7 min 30 sec", "6 min", "5 min"],
  ["2000 words", "13 min 20 sec", "10 min", "8 min", "6 min 40 sec"],
  ["3000 words", "20 min", "15 min", "12 min", "10 min"],
  ["5000 words", "33 min 20 sec", "25 min", "20 min", "16 min 40 sec"],
];

const pagesToReadingRows = [
  ["1 page", "250 words", "1 min 15 sec"],
  ["2 pages", "500 words", "2 min 30 sec"],
  ["5 pages", "1250 words", "6 min 15 sec"],
  ["10 pages", "2500 words", "12 min 30 sec"],
  ["20 pages", "5000 words", "25 min"],
  ["50 pages", "12500 words", "1 hr 2 min 30 sec"],
];

export function countWords(text: string) {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

export function formatReadingTime(decimalMinutes: number) {
  const totalSeconds = Math.round(decimalMinutes * 60);
  const hours = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;

  if (hours > 0) {
    if (mins === 0 && secs === 0) return `${hours} hr`;
    if (secs === 0) return `${hours} hr ${mins} min`;
    return `${hours} hr ${mins} min ${secs} sec`;
  }

  if (mins === 0) return `${secs} sec`;
  if (secs === 0) return `${mins} min`;
  return `${mins} min ${secs} sec`;
}

export function calculateReadingTime(wordCount: number, wpm: number) {
  if (!wordCount || !wpm || wordCount <= 0 || wpm <= 0) return null;
  return wordCount / wpm;
}

export function calculateReadingTimeFromPages(
  pages: number,
  wordsPerPage: number,
  wpm: number,
) {
  if (
    !pages ||
    !wordsPerPage ||
    !wpm ||
    pages <= 0 ||
    wordsPerPage <= 0 ||
    wpm <= 0
  ) {
    return null;
  }

  const estimatedWords = pages * wordsPerPage;
  const readingTimeMinutes = estimatedWords / wpm;

  return {
    estimatedWords,
    readingTimeMinutes,
  };
}

export function calculateWordsPerMinute(
  wordCount: number,
  minutes: number | "",
  seconds: number | "",
) {
  const totalMinutes = Number(minutes || 0) + Number(seconds || 0) / 60;

  if (!wordCount || !totalMinutes || wordCount <= 0 || totalMinutes <= 0) {
    return null;
  }

  return Math.round(wordCount / totalMinutes);
}

function toNumberInputValue(value: string | number): NumberValue {
  if (value === "") {
    return "";
  }

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : "";
}

function numberValue(value: NumberValue) {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function formatWords(value: number) {
  return Math.round(value).toLocaleString("en-US");
}

function getWordsPerMinute(speed: ReadingSpeedKey, customWpm: NumberValue) {
  if (speed === "custom") {
    return numberValue(customWpm) > 0 ? numberValue(customWpm) : null;
  }

  return readingSpeeds[speed];
}

function getWordsPerPage(pageType: WordsPerPageKey, customWordsPerPage: NumberValue) {
  if (pageType === "custom") {
    return numberValue(customWordsPerPage) > 0 ? numberValue(customWordsPerPage) : null;
  }

  return wordsPerPageOptions.find((option) => option.value === pageType)?.words || 250;
}

function ReadingTimeCalculatorPage() {
  const webApplicationSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Reading Time Calculator",
    applicationCategory: "EducationalApplication",
    operatingSystem: "All",
    url: buildCanonicalUrl(TOOL_PATH),
    description:
      "Free reading time calculator to estimate how long it takes to read text, words, pages, articles, essays, assignments, and books.",
    isAccessibleForFree: true,
  };

  return (
    <Container fluid className="reading-time-page" px={0}>
      <Seo
        title="Reading Time Calculator | How Long to Read Text, Words or Pages"
        description="Use our free Reading Time Calculator to estimate how long it takes to read text, words, or pages. Calculate read time by reading speed and word count."
        path={TOOL_PATH}
        ogTitle="Reading Time Calculator"
        ogDescription="Calculate how long to read text, words, pages, articles, essays, assignments, or books by reading speed."
        keywords={[
          "reading time calculator",
          "read time calculator",
          "how long to read",
          "reading time calculator pages",
          "calculate reading pages time",
          "words per minute calculator",
          "how long does it take to read 1000 words",
        ]}
        schema={[
          createBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Tools", path: TOOLS_PATH },
            { name: "Reading Time Calculator", path: TOOL_PATH },
          ]),
          createFaqSchema(faqs),
          webApplicationSchema,
        ]}
      />

      <Box className="reading-time-hero">
        <Container size="xl">
          <Stack gap="lg">
            <Breadcrumbs separator="/">
              <Anchor component={Link} to="/">
                Home
              </Anchor>
              <Anchor component={Link} to={TOOLS_PATH}>
                Tools
              </Anchor>
              <Text>Reading Time Calculator</Text>
            </Breadcrumbs>

            <SimpleGrid cols={{ base: 1, lg: 2 }} spacing={34}>
              <Stack gap="md" justify="center">
                <Group gap="sm">
                  <Badge color="green" radius="sm" size="lg">
                    Essay & Writing Tools
                  </Badge>
                  <Badge color="blue" radius="sm" size="lg" variant="light">
                    Free how long to read tool
                  </Badge>
                </Group>
                <ThemeIcon size={58} radius="md" color="green" variant="light">
                  <IconBook2 size={32} />
                </ThemeIcon>
                <Title order={1} className="reading-time-title">
                  Reading Time Calculator
                </Title>
                <Stack gap="xs" className="reading-time-intro">
                  <Text size="lg" c="dimmed">
                    Use our free Reading Time Calculator to estimate how long it
                    will take to read an article, essay, assignment, book
                    chapter, or study material. Paste your text, enter your word
                    count, or calculate reading time by number of pages.
                  </Text>
                  <Text c="dimmed">
                    You can calculate reading time by text, words, pages, and
                    reading speed in words per minute.
                  </Text>
                </Stack>
              </Stack>

              <Paper
                withBorder
                radius="md"
                p={{ base: "md", md: "xl" }}
                className="reading-time-panel"
              >
                <ReadingTimeCalculator />
              </Paper>
            </SimpleGrid>
          </Stack>
        </Container>
      </Box>

      <Container size="xl" py={48}>
        <main className="reading-time-content">
          <ReadingTimeSeoContent />
          <ReadingTimeCta />
          <RelatedToolsSection />
          <ReadingTimeFAQ />
        </main>
      </Container>
    </Container>
  );
}

export function ReadingTimeCalculator() {
  const [mode, setMode] = useState<ReadingMode>("text");
  const [text, setText] = useState("");
  const [wordCountInput, setWordCountInput] = useState<NumberValue>("");
  const [pageCountInput, setPageCountInput] = useState<NumberValue>("");
  const [pageType, setPageType] = useState<WordsPerPageKey>("standard");
  const [customWordsPerPage, setCustomWordsPerPage] = useState<NumberValue>(250);
  const [speed, setSpeed] = useState<ReadingSpeedKey>("average");
  const [customWpm, setCustomWpm] = useState<NumberValue>(200);
  const [wpmWordCount, setWpmWordCount] = useState<NumberValue>("");
  const [wpmMinutes, setWpmMinutes] = useState<NumberValue>(5);
  const [wpmSeconds, setWpmSeconds] = useState<NumberValue>(0);
  const wordsPerMinute = getWordsPerMinute(speed, customWpm);
  const wordsPerPage = getWordsPerPage(pageType, customWordsPerPage);
  const textWordCount = countWords(text);
  const numericWordCount = numberValue(wordCountInput);
  const numericPageCount = numberValue(pageCountInput);
  const wpmNumericWordCount = numberValue(wpmWordCount);
  const wpmTotalMinutes = numberValue(wpmMinutes) + numberValue(wpmSeconds) / 60;

  const result = useMemo(() => {
    if (mode === "wpm") {
      const readingWpm = calculateWordsPerMinute(
        wpmNumericWordCount,
        wpmMinutes,
        wpmSeconds,
      );

      return readingWpm
        ? {
            mode,
            wordsPerMinute: readingWpm,
          }
        : null;
    }

    if (!wordsPerMinute) {
      return null;
    }

    if (mode === "text") {
      const readingTime = calculateReadingTime(textWordCount, wordsPerMinute);

      return readingTime
        ? {
            mode,
            wordCount: textWordCount,
            wordsPerMinute,
            formattedTime: formatReadingTime(readingTime),
          }
        : null;
    }

    if (mode === "words") {
      const readingTime = calculateReadingTime(numericWordCount, wordsPerMinute);

      return readingTime
        ? {
            mode,
            wordCount: numericWordCount,
            wordsPerMinute,
            formattedTime: formatReadingTime(readingTime),
          }
        : null;
    }

    if (!wordsPerPage) {
      return null;
    }

    const pagesResult = calculateReadingTimeFromPages(
      numericPageCount,
      wordsPerPage,
      wordsPerMinute,
    );

    return pagesResult
      ? {
          mode,
          pages: numericPageCount,
          estimatedWords: pagesResult.estimatedWords,
          wordsPerMinute,
          formattedTime: formatReadingTime(pagesResult.readingTimeMinutes),
        }
      : null;
  }, [
    mode,
    numericPageCount,
    numericWordCount,
    textWordCount,
    wordsPerMinute,
    wordsPerPage,
    wpmMinutes,
    wpmNumericWordCount,
    wpmSeconds,
  ]);

  const validationMessage = useMemo(() => {
    if (mode === "wpm") {
      if (wpmNumericWordCount <= 0 || wpmTotalMinutes <= 0) {
        return "Enter a positive word count and reading time to calculate words per minute.";
      }

      return "";
    }

    if (!wordsPerMinute) {
      return "Enter a positive custom reading speed to see the estimate.";
    }

    if (mode === "text" && textWordCount <= 0) {
      return "Paste your text to see the reading time estimate.";
    }

    if (mode === "words" && numericWordCount <= 0) {
      return "Enter a positive word count to calculate reading time.";
    }

    if (mode === "pages") {
      if (numericPageCount <= 0) {
        return "Enter a positive page count to calculate reading time.";
      }

      if (!wordsPerPage) {
        return "Enter a positive custom words-per-page value.";
      }
    }

    return "";
  }, [
    mode,
    numericPageCount,
    numericWordCount,
    textWordCount,
    wordsPerMinute,
    wordsPerPage,
    wpmNumericWordCount,
    wpmTotalMinutes,
  ]);

  return (
    <Stack gap="lg" component="section" aria-label="Reading time calculator">
      <Stack gap={6}>
        <Title order={2}>Calculate Reading Time</Title>
        <Text c="dimmed">
          Paste text, enter words, estimate page reading time, or calculate your
          own reading speed.
        </Text>
      </Stack>

      <SegmentedControl
        fullWidth
        value={mode}
        onChange={(value) => setMode(value as ReadingMode)}
        className="reading-time-mode-control"
        data={[
          { value: "text", label: "Text to Reading Time" },
          { value: "words", label: "Words to Reading Time" },
          { value: "pages", label: "Pages to Reading Time" },
          { value: "wpm", label: "Words Per Minute Calculator" },
        ]}
        aria-label="Reading calculator mode"
      />

      {mode === "text" ? (
        <TextToReadingTimeMode text={text} onTextChange={setText} wordCount={textWordCount} />
      ) : null}

      {mode === "words" ? (
        <WordsToReadingTimeMode
          wordCount={wordCountInput}
          onWordCountChange={setWordCountInput}
        />
      ) : null}

      {mode === "pages" ? (
        <PagesToReadingTimeMode
          pageCount={pageCountInput}
          pageType={pageType}
          customWordsPerPage={customWordsPerPage}
          onPageCountChange={setPageCountInput}
          onPageTypeChange={setPageType}
          onCustomWordsPerPageChange={setCustomWordsPerPage}
        />
      ) : null}

      {mode === "wpm" ? (
        <WordsPerMinuteCalculatorMode
          wordCount={wpmWordCount}
          minutes={wpmMinutes}
          seconds={wpmSeconds}
          onWordCountChange={setWpmWordCount}
          onMinutesChange={setWpmMinutes}
          onSecondsChange={setWpmSeconds}
        />
      ) : (
        <ReadingSpeedSelector
          speed={speed}
          customWpm={customWpm}
          onSpeedChange={setSpeed}
          onCustomWpmChange={setCustomWpm}
        />
      )}

      {validationMessage ? (
        <Alert color="yellow" radius="md" variant="light" icon={<IconInfoCircle size={18} />}>
          {validationMessage}
        </Alert>
      ) : null}

      {result ? (
        <>
          <ReadingTimeResult result={result} />
          {result.mode !== "wpm" ? (
            <ReadingSpeedComparison wordCount={getComparisonWordCount(result)} />
          ) : null}
          <Box className="reading-time-tip-box tip-box">
            <Group gap="md" align="flex-start" wrap="nowrap">
              <ThemeIcon color="blue" radius="md" variant="light">
                <IconSparkles size={20} />
              </ThemeIcon>
              <Stack gap={4}>
                <Title order={3}>Study Planning Tip</Title>
                <Text c="dimmed">
                  If you are reading academic material, add extra time for
                  highlighting, note-taking, understanding concepts, and
                  reviewing important points.
                </Text>
              </Stack>
            </Group>
          </Box>
        </>
      ) : null}
    </Stack>
  );
}

function getComparisonWordCount(
  result:
    | {
        mode: "text" | "words";
        wordCount: number;
      }
    | {
        mode: "pages";
        estimatedWords: number;
      }
    | {
        mode: "wpm";
        wordsPerMinute: number;
      },
) {
  if (result.mode === "pages") {
    return result.estimatedWords;
  }

  if (result.mode === "wpm") {
    return 0;
  }

  return result.wordCount;
}

function TextToReadingTimeMode({
  text,
  onTextChange,
  wordCount,
}: {
  text: string;
  onTextChange: (value: string) => void;
  wordCount: number;
}) {
  return (
    <Stack gap="sm">
      <Textarea
        label="Paste your text"
        placeholder="Paste your article, assignment, essay, chapter, or reading material here..."
        value={text}
        onChange={(event) => onTextChange(event.currentTarget.value)}
        minRows={7}
        autosize
        aria-label="Paste your text"
      />
      <Group justify="space-between" gap="sm" className="reading-time-live-count">
        <Text size="sm" c="dimmed">
          Word count updates instantly as you type.
        </Text>
        <Badge color="green" radius="sm" size="lg">
          {formatWords(wordCount)} words
        </Badge>
      </Group>
    </Stack>
  );
}

function WordsToReadingTimeMode({
  wordCount,
  onWordCountChange,
}: {
  wordCount: NumberValue;
  onWordCountChange: (value: NumberValue) => void;
}) {
  return (
    <NumberInput
      label="Word Count"
      placeholder="Enter word count, e.g. 1000"
      value={wordCount}
      min={0}
      allowNegative={false}
      onChange={(value) => onWordCountChange(toNumberInputValue(value))}
      aria-label="Word count"
    />
  );
}

function PagesToReadingTimeMode({
  pageCount,
  pageType,
  customWordsPerPage,
  onPageCountChange,
  onPageTypeChange,
  onCustomWordsPerPageChange,
}: {
  pageCount: NumberValue;
  pageType: WordsPerPageKey;
  customWordsPerPage: NumberValue;
  onPageCountChange: (value: NumberValue) => void;
  onPageTypeChange: (value: WordsPerPageKey) => void;
  onCustomWordsPerPageChange: (value: NumberValue) => void;
}) {
  return (
    <SimpleGrid cols={{ base: 1, sm: pageType === "custom" ? 3 : 2 }} spacing="md">
      <NumberInput
        label="Number of Pages"
        placeholder="Enter pages, e.g. 10"
        value={pageCount}
        min={0}
        allowNegative={false}
        onChange={(value) => onPageCountChange(toNumberInputValue(value))}
        aria-label="Number of pages"
      />
      <Select
        label="Words Per Page"
        data={wordsPerPageOptions.map(({ value, label }) => ({ value, label }))}
        value={pageType}
        onChange={(value) => onPageTypeChange((value as WordsPerPageKey) || "standard")}
        allowDeselect={false}
        aria-label="Words per page"
      />
      {pageType === "custom" ? (
        <NumberInput
          label="Custom Words Per Page"
          placeholder="Enter words per page"
          value={customWordsPerPage}
          min={1}
          allowNegative={false}
          onChange={(value) => onCustomWordsPerPageChange(toNumberInputValue(value))}
          aria-label="Custom words per page"
        />
      ) : null}
    </SimpleGrid>
  );
}

function WordsPerMinuteCalculatorMode({
  wordCount,
  minutes,
  seconds,
  onWordCountChange,
  onMinutesChange,
  onSecondsChange,
}: {
  wordCount: NumberValue;
  minutes: NumberValue;
  seconds: NumberValue;
  onWordCountChange: (value: NumberValue) => void;
  onMinutesChange: (value: NumberValue) => void;
  onSecondsChange: (value: NumberValue) => void;
}) {
  return (
    <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
      <NumberInput
        label="Number of Words Read"
        placeholder="Enter number of words read"
        value={wordCount}
        min={0}
        allowNegative={false}
        onChange={(value) => onWordCountChange(toNumberInputValue(value))}
        aria-label="Number of words read"
      />
      <NumberInput
        label="Reading Time - Minutes"
        placeholder="Minutes"
        value={minutes}
        min={0}
        allowNegative={false}
        onChange={(value) => onMinutesChange(toNumberInputValue(value))}
        aria-label="Reading time in minutes"
      />
      <NumberInput
        label="Reading Time - Seconds"
        placeholder="Seconds"
        value={seconds}
        min={0}
        max={59}
        allowNegative={false}
        onChange={(value) => onSecondsChange(toNumberInputValue(value))}
        aria-label="Reading time in seconds"
      />
    </SimpleGrid>
  );
}

function ReadingSpeedSelector({
  speed,
  customWpm,
  onSpeedChange,
  onCustomWpmChange,
}: {
  speed: ReadingSpeedKey;
  customWpm: NumberValue;
  onSpeedChange: (value: ReadingSpeedKey) => void;
  onCustomWpmChange: (value: NumberValue) => void;
}) {
  return (
    <Stack gap="sm">
      <SimpleGrid cols={{ base: 1, sm: speed === "custom" ? 2 : 1 }} spacing="md">
        <Select
          label="Reading Speed"
          data={speedOptions}
          value={speed}
          onChange={(value) => onSpeedChange((value as ReadingSpeedKey) || "average")}
          allowDeselect={false}
          aria-label="Reading speed"
        />
        {speed === "custom" ? (
          <NumberInput
            label="Custom WPM"
            placeholder="Enter your reading speed"
            value={customWpm}
            min={1}
            allowNegative={false}
            onChange={(value) => onCustomWpmChange(toNumberInputValue(value))}
            aria-label="Custom reading speed"
          />
        ) : null}
      </SimpleGrid>
      <Text size="sm" c="dimmed" className="reading-time-helper">
        Most adults read around 200 to 250 words per minute, but reading speed
        can vary depending on topic difficulty, focus, and reading purpose.
      </Text>
    </Stack>
  );
}

function ReadingTimeResult({
  result,
}: {
  result:
    | {
        mode: "text" | "words";
        wordCount: number;
        wordsPerMinute: number;
        formattedTime: string;
      }
    | {
        mode: "pages";
        pages: number;
        estimatedWords: number;
        wordsPerMinute: number;
        formattedTime: string;
      }
    | {
        mode: "wpm";
        wordsPerMinute: number;
      };
}) {
  return (
    <Box className="reading-time-result" aria-live="polite">
      <Stack gap="sm">
        <Title order={2}>Your Reading Time Estimate</Title>
        {result.mode === "text" ? (
          <>
            <Text>
              Your text has <strong>{formatWords(result.wordCount)}</strong>{" "}
              words.
            </Text>
            <Text>
              At <strong>{result.wordsPerMinute} words per minute</strong>, it
              will take approximately <strong>{result.formattedTime}</strong> to
              read.
            </Text>
            <Text c="dimmed">
              If the text is technical, academic, or research-heavy, add extra
              time for understanding, note-taking, and rereading.
            </Text>
          </>
        ) : null}

        {result.mode === "words" ? (
          <>
            <Text>
              <strong>{formatWords(result.wordCount)} words</strong> will take
              approximately <strong>{result.formattedTime}</strong> to read at{" "}
              {result.wordsPerMinute} words per minute.
            </Text>
            <Text c="dimmed">
              If the text is technical, academic, or research-heavy, add extra
              time for understanding, note-taking, and rereading.
            </Text>
          </>
        ) : null}

        {result.mode === "pages" ? (
          <>
            <Text>
              <strong>{formatWords(result.pages)} pages</strong> is
              approximately <strong>{formatWords(result.estimatedWords)} words</strong>.
            </Text>
            <Text>
              At <strong>{result.wordsPerMinute} words per minute</strong>, it
              will take approximately <strong>{result.formattedTime}</strong> to
              read.
            </Text>
          </>
        ) : null}

        {result.mode === "wpm" ? (
          <>
            <Text>
              Your reading speed is approximately{" "}
              <strong>{formatWords(result.wordsPerMinute)} words per minute</strong>.
            </Text>
            <Text c="dimmed">
              You can use this reading speed in the calculator above for a more
              accurate reading time estimate.
            </Text>
          </>
        ) : null}
      </Stack>
    </Box>
  );
}

function ReadingSpeedComparison({ wordCount }: { wordCount: number }) {
  const comparisonItems = (Object.keys(readingSpeeds) as PresetSpeedKey[]).map(
    (key) => ({
      label: speedLabels[key],
      value: formatReadingTime(wordCount / readingSpeeds[key]),
    }),
  );

  return (
    <Box className="reading-time-comparison">
      <Stack gap="sm">
        <Title order={3}>Reading Time by Speed</Title>
        <ul>
          {comparisonItems.map((item) => (
            <li key={item.label}>
              <strong>{item.label}:</strong> {item.value}
            </li>
          ))}
        </ul>
      </Stack>
    </Box>
  );
}

function ReadingTimeSeoContent() {
  return (
    <>
      <SeoTextSection title="How Does the Reading Time Calculator Work?">
        <Text>
          The Reading Time Calculator estimates how long it will take to read a
          piece of text by dividing the total word count by your reading speed.
          For example, if your text has 1000 words and you read at 200 words per
          minute, it will take about 5 minutes to read.
        </Text>
      </SeoTextSection>

      <SeoTextSection title="How Long Does It Take to Read 1000 Words?">
        <Text>
          1000 words usually takes around 4 to 7 minutes to read, depending on
          reading speed. At an average reading speed of 200 words per minute,
          1000 words takes about 5 minutes to read.
        </Text>
      </SeoTextSection>

      <SeoTextSection title="How Long Does It Take to Read One Page?">
        <Text>
          One standard page usually has around 250 to 500 words. A 250-word page
          takes about 1 to 2 minutes to read, while a 500-word page takes about 2
          to 3 minutes for an average reader.
        </Text>
      </SeoTextSection>

      <SeoTextSection title="How to Calculate Reading Time Based on Pages">
        <Text>
          To calculate reading time based on pages, first estimate the number of
          words per page. Then multiply the page count by words per page and
          divide the result by your reading speed. For example, 10 pages with
          250 words per page equals 2500 words. At 200 words per minute, it will
          take about 12 minutes and 30 seconds to read.
        </Text>
      </SeoTextSection>

      <EstimateTable
        title="Common Reading Time Estimates"
        headers={[
          "Word Count",
          "Slow 150 WPM",
          "Average 200 WPM",
          "Good 250 WPM",
          "Fast 300 WPM",
        ]}
        rows={readingTimeRows}
      />

      <EstimateTable
        title="Pages to Reading Time Estimates"
        headers={["Pages", "Approx. Words", "Average Reading Time 200 WPM"]}
        rows={pagesToReadingRows}
      />

      <SeoTextSection title="Who Can Use This Reading Time Calculator?">
        <Text>
          This calculator is useful for students, teachers, researchers,
          bloggers, content writers, readers, and anyone who wants to estimate
          how long reading material will take. Students can use it to plan study
          sessions, estimate assignment reading time, and manage deadlines more
          effectively.
        </Text>
      </SeoTextSection>
    </>
  );
}

function SeoTextSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="reading-time-section">
      <Stack gap="md">
        <Title order={2}>{title}</Title>
        {children}
      </Stack>
    </section>
  );
}

function EstimateTable({
  title,
  headers,
  rows,
}: {
  title: string;
  headers: string[];
  rows: string[][];
}) {
  return (
    <section className="reading-time-section">
      <Stack gap="md">
        <Title order={2}>{title}</Title>
        <Box className="reading-time-table-wrap">
          <Table striped highlightOnHover withTableBorder withColumnBorders>
            <Table.Thead>
              <Table.Tr>
                {headers.map((header) => (
                  <Table.Th key={header}>{header}</Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => (
                <Table.Tr key={row.join("-")}>
                  {row.map((cell) => (
                    <Table.Td key={cell}>{cell}</Table.Td>
                  ))}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Box>
      </Stack>
    </section>
  );
}

function ReadingTimeCta() {
  return (
    <section className="reading-time-cta cta-box">
      <Group justify="space-between" align="center" gap="lg">
        <Group gap="md" align="flex-start" wrap="nowrap">
          <ThemeIcon radius="md" size={54} color="blue">
            <IconSchool size={28} />
          </ThemeIcon>
          <Stack gap={4}>
            <Title order={2}>Need Help Understanding Your Reading Material?</Title>
            <Text c="dimmed" className="reading-time-cta-copy">
              If your reading material is long, technical, or difficult to
              understand, SubjectBuddy can connect you with academic support
              experts who can help with summaries, explanations, assignments,
              and study guidance.
            </Text>
          </Stack>
        </Group>
        <Group gap="sm">
          <Button
            component={Link}
            to="/assignment-help"
            radius="md"
            className="cta-button"
            rightSection={<IconArrowRight size={18} />}
          >
            Get Assignment Help
          </Button>
          <Anchor component={Link} to="/study-help" className="secondary-link">
            Get Study Help
          </Anchor>
        </Group>
      </Group>
    </section>
  );
}

function RelatedToolsSection() {
  const relatedItems = [
    {
      title: "Speech Time Calculator",
      href: "/tools/speech-time-calculator",
      description: "Estimate how long your speech, script, or presentation will take.",
      icon: <IconCalculator size={20} />,
    },
    {
      title: "Words to Pages Calculator",
      href: "/tools/words-to-pages-calculator",
      description: "Turn assignment word counts into page estimates.",
      icon: <IconCalculator size={20} />,
    },
    {
      title: "Pages to Words Calculator",
      href: "/tools/pages-to-words-calculator",
      description: "Estimate word count from page limits and academic formatting.",
      icon: <IconCalculator size={20} />,
    },
    {
      title: "Assignment Help",
      href: "/assignment-help",
      description: "Get academic support with long readings, deadlines, and coursework.",
      icon: <IconSchool size={20} />,
    },
    {
      title: "Essay Writing Help",
      href: "/essay-writing-help",
      description: "Plan essays after estimating reading and research time.",
      icon: <IconFileText size={20} />,
    },
    {
      title: "Study Help",
      href: "/study-help",
      description: "Get help turning reading notes into study plans and clear next steps.",
      icon: <IconBook2 size={20} />,
    },
    {
      title: "How Long Does It Take to Read 1000 Words?",
      href: "/blog/how-long-does-it-take-to-read-1000-words",
      description: "Use the supporting guide for 1000-word reading estimates.",
      icon: <IconFileText size={20} />,
    },
    {
      title: "How Many Pages Is 1000 Words?",
      href: "/blog/how-many-pages-is-1000-words",
      description: "Compare word count, page count, and academic formatting.",
      icon: <IconFileText size={20} />,
    },
    {
      title: "How to Read Faster for Studying",
      href: "/blog/how-to-read-faster-for-studying",
      description: "Review study reading strategies after calculating your read time.",
      icon: <IconFileText size={20} />,
    },
  ];

  // TODO: Add live pages or redirects if /study-help or the requested reading
  // blog slugs remain served under alternate routes.
  return (
    <section className="reading-time-section">
      <Stack gap="md">
        <Title order={2}>Related Tools and Guides</Title>
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          {relatedItems.map((item) => (
            <Paper
              key={item.href}
              component={Link}
              to={item.href}
              withBorder
              radius="md"
              p="lg"
              className="reading-time-link-card"
            >
              <Group gap="md" wrap="nowrap" align="flex-start">
                <ThemeIcon color="green" variant="light" radius="md">
                  {item.icon}
                </ThemeIcon>
                <Stack gap={4}>
                  <Text fw={800}>{item.title}</Text>
                  <Text size="sm" c="dimmed">
                    {item.description}
                  </Text>
                </Stack>
              </Group>
            </Paper>
          ))}
        </SimpleGrid>
      </Stack>
    </section>
  );
}

function ReadingTimeFAQ() {
  return (
    <section className="reading-time-section">
      <Stack gap="md">
        <Title order={2}>FAQs About Reading Time Calculator</Title>
        {faqs.map((faq) => (
          <Paper key={faq.question} withBorder radius="md" p="lg">
            <Stack gap={6}>
              <Title order={3}>{faq.question}</Title>
              <Text c="dimmed" style={{ lineHeight: 1.8 }}>
                {faq.answer}
              </Text>
            </Stack>
          </Paper>
        ))}
      </Stack>
    </section>
  );
}

export default ReadingTimeCalculatorPage;
